import { memo, useMemo, useState, useCallback } from 'react';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { OnChangeFn, PaginationState, ColumnDef } from '@tanstack/react-table';
import { formatDate } from '@/shared/lib/utils/formatDate';
import {
  useGetAssignableStudentsQuery,
  useAssignStudentsMutation,
  Enrollment,
} from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { IndeterminateCheckbox } from '@/shared/ui/IndeterminateCheckbox';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import { Svg } from '@/shared/ui/Svg';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import SearchIcon from '@/shared/assets/icons/search.svg';
import styles from './DistributeStudentsModal.module.scss';

interface DistributeStudentsModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakeId: number;
  groupNumber: number;
  onSuccess?: () => void;
}

const getStatusColor = (state: string) => {
  const stateLower = state.toLowerCase();
  if (stateLower === 'active') return 'bg-green-positive text-green-secondary';
  if (stateLower === 'deferred') return 'bg-purple-secondary text-purple-600';
  if (stateLower === 'graduated') return 'bg-yellow-secondary text-yellow-tertiary';
  return 'bg-grey-10 text-grey-secondary';
};

const getStatusLabel = (state: string) => {
  const stateLower = state.toLowerCase();
  if (stateLower === 'active') return 'Active';
  if (stateLower === 'deferred') return 'Deferred';
  if (stateLower === 'graduated') return 'Graduated';
  return state;
};

export const DistributeStudentsModal = memo(
  ({ isOpen, onOpenChange, intakeId, groupNumber, onSuccess }: DistributeStudentsModalProps) => {
    const { toast } = useToast();
    const [search, setSearch] = useState('');
    const [selectedRows, setSelectedRows] = useState<Enrollment[]>([]);
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });

    const { data, isLoading, refetch } = useGetAssignableStudentsQuery(
      { intakeId, groupNumber },
      { skip: !isOpen },
    );

    const [assignStudents, { isLoading: isAssigning }] = useAssignStudentsMutation();

    const enrollments = useMemo(() => data?.data?.enrollments || [], [data?.data?.enrollments]);

    const filteredEnrollments = useMemo(() => {
      if (!search.trim()) return enrollments;
      const searchLower = search.toLowerCase();
      return enrollments.filter(
        (enrollment) =>
          enrollment.firstName.toLowerCase().includes(searchLower) ||
          enrollment.lastName.toLowerCase().includes(searchLower) ||
          enrollment.email.toLowerCase().includes(searchLower) ||
          enrollment.username.toLowerCase().includes(searchLower),
      );
    }, [enrollments, search]);

    const columns: ColumnDef<Enrollment>[] = useMemo(
      () => [
        {
          id: 'select',
          header: ({ table }) => (
            <IndeterminateCheckbox
              uniqueKey="all-students-select"
              name="all-students"
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
          ),
          cell: ({ row }) => (
            <IndeterminateCheckbox
              uniqueKey={`student-select-${row.original.id}`}
              name="student"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          ),
          size: 64,
        },
        {
          header: 'ID',
          accessorKey: 'username',
          size: 110,
          cell: ({ row }) => (
            <span className="text-black-headline">
              {row.original.userId || `IHCL${row.original.id}`}
            </span>
          ),
        },
        {
          header: 'User',
          accessorFn: ({ firstName, lastName }) => `${firstName} ${lastName}`,
          size: 240,
          cell: ({ row }) => (
            <div className="flex w-0 min-w-full flex-col">
              <span className="break-words text-black-headline font-medium">
                {row.original.firstName} {row.original.lastName}
              </span>
              <span className="break-all text-grey-secondary text-sm">{row.original.email}</span>
            </div>
          ),
        },
        {
          header: 'Registration Date',
          accessorKey: 'registrationDate',
          size: 150,
          cell: ({ row }) => (
            <span className="text-black-headline flex">01.01.2024</span>
          ),
        },
        {
          header: 'Status',
          accessorKey: 'state',
          size: 110,
          cell: ({ row }) => {
            const statusColor = getStatusColor(row.original.state);
            const statusLabel = getStatusLabel(row.original.state);
            return (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                {statusLabel}
              </span>
            );
          },
        },
      ],
      [],
    );

    const handleSelectedItems = useCallback((items: Enrollment[]) => {
      setSelectedRows(items);
    }, []);

    const handleAssign = async () => {
      if (selectedRows.length === 0) {
        toast({
          title: 'Error',
          description: 'Please select at least one student',
          color: 'danger',
        });
        return;
      }

      try {
        const result = await assignStudents({
          intakeId,
          groupNumber,
          body: {
            enrollmentIds: selectedRows.map((student) => student.id),
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Students assigned successfully',
          color: 'success',
        });

        handleClose();
        refetch();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to assign students',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      setSearch('');
      setSelectedRows([]);
      setPagination({ pageIndex: 0, pageSize: 10 });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="5xl"
        classNames={{
          base: 'p-[36px] flex flex-col gap-5 max-w-[772px]',
          closeButton: styles.closeBtn,
        }}
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className={styles.title}>
                Distribute the students
                <span className="ml-5 text-[#757575] font-normal text-base">
                  Group {data?.data.targetGroup.number} - Term {data?.data.targetTermNumber}
                </span>
              </ModalHeader>
              <ModalBody className="p-0 -mr-4 pr-4 flex flex-col gap-4">
                <div>
                  <CustomInput
                    className={searchInputClassName}
                    startContent={<Svg Icon={SearchIcon} />}
                    placeholder="Search by name"
                    value={search}
                    onValueChange={setSearch}
                  />
                </div>

                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-grey-secondary">Loading students...</div>
                  </div>
                ) : (
                  <ContainerTable<Enrollment>
                    data={filteredEnrollments}
                    columns={columns}
                    pagination={pagination}
                    setPagination={setPagination as OnChangeFn<PaginationState>}
                    hideTopActions={true}
                    onSelectedItemsChange={handleSelectedItems}
                  />
                )}
              </ModalBody>
              <ModalFooter className="gap-4 py-0 justify-between">
                <div className="flex-1" />
                <AppButton
                  color="black"
                  onPress={handleAssign}
                  isLoading={isAssigning}
                  disabled={selectedRows.length === 0}
                  className="bg-[#211D55] text-white"
                >
                  Add students
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

DistributeStudentsModal.displayName = 'DistributeStudentsModal';
